import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2,
  Plus,
  Calendar as CalendarIcon,
  Pencil,
  Trash2,
  ImagePlus,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";

type ImportantDate = {
  id: string;
  user_id: string;
  title: string;
  date: string;
  photo_url: string | null;
  notes: string | null;
};

const PRESET_TITLES = [
  "Birthday",
  "Wedding Anniversary",
  "Engagement Anniversary",
  "Graduation",
  "Work Anniversary",
  "Name Day",
  "Baby's Birthday",
  "Pet's Birthday",
  "Mother's Day",
  "Father's Day",
];

const CUSTOM_VALUE = "__custom__";

interface Props {
  userId: string;
}

const MyDatesTab = ({ userId }: Props) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [items, setItems] = useState<ImportantDate[]>([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ImportantDate | null>(null);
  const [titleSelect, setTitleSelect] = useState<string>(PRESET_TITLES[0]);
  const [customTitle, setCustomTitle] = useState("");
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("user_important_dates" as any)
      .select("*")
      .eq("user_id", userId)
      .order("date", { ascending: true });
    if (!error && data) setItems(data as unknown as ImportantDate[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const resetForm = () => {
    setEditing(null);
    setTitleSelect(PRESET_TITLES[0]);
    setCustomTitle("");
    setDate("");
    setNotes("");
    setPhotoUrl(null);
  };

  const openAdd = () => {
    resetForm();
    setOpen(true);
  };

  const openEdit = (item: ImportantDate) => {
    setEditing(item);
    if (PRESET_TITLES.includes(item.title)) {
      setTitleSelect(item.title);
      setCustomTitle("");
    } else {
      setTitleSelect(CUSTOM_VALUE);
      setCustomTitle(item.title);
    }
    setDate(item.date);
    setNotes(item.notes || "");
    setPhotoUrl(item.photo_url);
    setOpen(true);
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!ext || !["jpg", "jpeg", "png", "webp"].includes(ext)) {
      toast({ variant: "destructive", title: "Invalid file", description: "Use JPG, PNG, or WebP." });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ variant: "destructive", title: "Too large", description: "Maximum 5 MB." });
      return;
    }
    setUploading(true);
    const path = `${userId}/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from("important-date-photos").upload(path, file);
    if (error) {
      toast({ variant: "destructive", title: "Upload failed", description: error.message });
      setUploading(false);
      return;
    }
    const { data } = supabase.storage.from("important-date-photos").getPublicUrl(path);
    setPhotoUrl(data.publicUrl);
    setUploading(false);
  };

  const handleSave = async () => {
    const title = titleSelect === CUSTOM_VALUE ? customTitle.trim() : titleSelect;
    if (!title) {
      toast({ variant: "destructive", title: "Missing title", description: "Please give this date a title." });
      return;
    }
    if (!date) {
      toast({ variant: "destructive", title: "Missing date", description: "Please pick a date." });
      return;
    }
    setSaving(true);
    const payload = {
      user_id: userId,
      title,
      date,
      notes: notes.trim() || null,
      photo_url: photoUrl,
    };
    let error;
    if (editing) {
      ({ error } = await supabase
        .from("user_important_dates" as any)
        .update(payload)
        .eq("id", editing.id));
    } else {
      ({ error } = await supabase.from("user_important_dates" as any).insert(payload));
    }
    setSaving(false);
    if (error) {
      toast({ variant: "destructive", title: "Error", description: "Could not save." });
      return;
    }
    toast({ title: "Saved!", description: "Your important date has been saved." });
    setOpen(false);
    resetForm();
    load();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const { error } = await supabase
      .from("user_important_dates" as any)
      .delete()
      .eq("id", deleteId);
    if (error) {
      toast({ variant: "destructive", title: "Error", description: "Could not delete." });
    } else {
      toast({ title: "Deleted", description: "Date removed." });
      load();
    }
    setDeleteId(null);
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const daysUntil = (iso: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(iso + "T00:00:00");
    const next = new Date(today.getFullYear(), d.getMonth(), d.getDate());
    if (next < today) next.setFullYear(today.getFullYear() + 1);
    const diff = Math.round((next.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (diff === 0) return t("dates.today", { defaultValue: "Today" });
    if (diff === 1) return t("dates.tomorrow", { defaultValue: "Tomorrow" });
    return t("dates.inDays", { defaultValue: "In {{count}} days", count: diff });
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <p className="text-sm text-muted-foreground">
          {t("dates.description", {
            defaultValue:
              "Add the dates that matter most to you — birthdays, anniversaries, and other special moments. Friends and family will know when to celebrate you.",
          })}
        </p>
        <Button
          onClick={openAdd}
          size="sm"
          className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shrink-0"
        >
          <Plus size={16} className="mr-1" />
          {t("dates.add", { defaultValue: "Add date" })}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-muted-foreground" size={20} />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-secondary/30 px-6 py-12 text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-background flex items-center justify-center mb-3 border border-border">
            <CalendarIcon size={20} className="text-muted-foreground" />
          </div>
          <h3 className="font-serif text-lg text-foreground mb-1">
            {t("dates.emptyTitle", { defaultValue: "No dates yet" })}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {t("dates.emptyBody", {
              defaultValue: "Add your first important date to get started.",
            })}
          </p>
          <Button onClick={openAdd} variant="outline" className="rounded-full">
            <Plus size={16} className="mr-1" />
            {t("dates.add", { defaultValue: "Add date" })}
          </Button>
        </div>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {items.map((item) => {
            const d = new Date(item.date + "T00:00:00");
            const day = d.getDate();
            const month = d.toLocaleDateString(undefined, { month: "short" });
            return (
              <li
                key={item.id}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex">
                  {/* Photo or date block */}
                  {item.photo_url ? (
                    <div className="relative h-28 w-28 shrink-0 overflow-hidden bg-muted">
                      <img
                        src={item.photo_url}
                        alt=""
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5 text-center">
                        <div className="text-[10px] uppercase tracking-wider text-white/90 leading-none">
                          {month}
                        </div>
                        <div className="text-base font-serif text-white leading-tight">
                          {day}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="h-28 w-28 shrink-0 bg-secondary flex flex-col items-center justify-center border-r border-border">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground leading-none mb-1">
                        {month}
                      </div>
                      <div className="text-3xl font-serif text-primary leading-none">
                        {day}
                      </div>
                    </div>
                  )}

                  {/* Content */}
                  <div className="flex-1 min-w-0 p-4">
                    <h3 className="font-serif text-base text-foreground truncate">
                      {item.title}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {formatDate(item.date)}
                    </p>
                    <p className="text-xs font-medium text-primary mt-1.5">
                      {daysUntil(item.date)}
                    </p>
                    {item.notes && (
                      <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">
                        {item.notes}
                      </p>
                    )}
                  </div>
                </div>

                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => openEdit(item)}
                    className="h-7 w-7 rounded-full bg-background/90 backdrop-blur border border-border flex items-center justify-center text-muted-foreground hover:text-foreground"
                    aria-label="Edit"
                  >
                    <Pencil size={12} />
                  </button>
                  <button
                    onClick={() => setDeleteId(item.id)}
                    className="h-7 w-7 rounded-full bg-background/90 backdrop-blur border border-border flex items-center justify-center text-muted-foreground hover:text-destructive"
                    aria-label="Delete"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif">
              {editing
                ? t("dates.editTitle", { defaultValue: "Edit date" })
                : t("dates.addTitle", { defaultValue: "Add an important date" })}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">
                {t("dates.fieldTitle", { defaultValue: "What's the occasion?" })}
              </Label>
              <Select value={titleSelect} onValueChange={setTitleSelect}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRESET_TITLES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                  <SelectItem value={CUSTOM_VALUE}>
                    {t("dates.custom", { defaultValue: "Custom…" })}
                  </SelectItem>
                </SelectContent>
              </Select>
              {titleSelect === CUSTOM_VALUE && (
                <Input
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  placeholder={t("dates.customPlaceholder", { defaultValue: "Enter a custom title" })}
                  maxLength={60}
                  className="mt-2"
                />
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium">
                {t("dates.fieldDate", { defaultValue: "Date" })}
              </Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium">
                {t("dates.fieldPhoto", { defaultValue: "Photo (optional)" })}
              </Label>
              {photoUrl ? (
                <div className="relative inline-block">
                  <img
                    src={photoUrl}
                    alt=""
                    className="h-24 w-24 rounded-lg object-cover border border-border"
                  />
                  <button
                    type="button"
                    onClick={() => setPhotoUrl(null)}
                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-background border border-border flex items-center justify-center text-muted-foreground hover:text-destructive"
                    aria-label="Remove photo"
                  >
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className={cn(
                    "h-24 w-24 rounded-lg border-2 border-dashed border-border bg-secondary/40 flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors",
                    uploading && "opacity-50"
                  )}
                >
                  {uploading ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <>
                      <ImagePlus size={18} />
                      <span className="text-[10px] mt-1">
                        {t("dates.upload", { defaultValue: "Upload" })}
                      </span>
                    </>
                  )}
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                hidden
                onChange={handleUpload}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium">
                {t("dates.fieldNotes", { defaultValue: "Notes (optional)" })}
              </Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t("dates.notesPlaceholder", {
                  defaultValue: "Anything friends should know…",
                })}
                maxLength={300}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => { setOpen(false); resetForm(); }}
              className="rounded-full"
            >
              {t("dates.cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {saving && <Loader2 size={14} className="animate-spin mr-2" />}
              {t("dates.save", { defaultValue: "Save date" })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("dates.deleteTitle", { defaultValue: "Delete this date?" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("dates.deleteBody", {
                defaultValue: "This can't be undone.",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("dates.cancel", { defaultValue: "Cancel" })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("dates.delete", { defaultValue: "Delete" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MyDatesTab;
